import { $color, UIColor } from '../ui/color';
import { yBisect } from '../y/math';
import { $time, YTimeScale } from '../y/time';

const e10 = Math.sqrt(50), e5 = Math.sqrt(10), e2 = Math.sqrt(2);

function linearRange(a: number, b: number, step: number) {
    if(step === 0) return [];
    const n = Math.max(0, Math.ceil((b - a) / step));
    const ret = new Array<number>(n);

    for(let i = 0; i < n; i++) {
        ret[i] = a + i * step;
    }

    return ret;
}

function linearTickStep(a: number, b: number, n: number, decimal?: boolean) {
    if(a === b) return 1;
    const step0 = Math.abs(a - b) / n;
    let step1 = Math.pow(10, Math.floor(Math.log(step0) * Math.LOG10E));
    const error = step0 / step1;
    if(error >= e10)
        step1 *= 10;
    else if(error >= e5)
        step1 *= 5;
    else if(error >= e2)
        step1 *= 2;
    if(step1 < 1 && !decimal) step1 = 1;
    return b < a ? -step1 : step1;
}

type TickInterval = [YTimeScale, number]

const tickIntervals: TickInterval[] = [
    [$time.second, 1],
    [$time.second, 5],
    [$time.second, 15],
    [$time.second, 30],
    [$time.minute, 1],
    [$time.minute, 5],
    [$time.minute, 15],
    [$time.minute, 30],
    [$time.hour, 1],
    [$time.hour, 3],
    [$time.hour, 6],
    [$time.hour, 12],
    [$time.day, 1],
    [$time.day, 2],
    [$time.week, 1],
    [$time.month, 1],
    [$time.month, 3],
    [$time.month, 6],
    [$time.year, 1]
];

function timeTickInterval(a: number, b: number, n: number): TickInterval {
    const target = Math.abs(b - a) / n;
    // const sign = a < b ? 1 : -1
    const i = yBisect(tickIntervals, target, (t) => t[0].duration * t[1]);
    let ret: TickInterval;
    if(i === tickIntervals.length) {
        ret = [$time.year, linearTickStep(a / $time.year.duration, b / $time.year.duration, n)];
    } else if(i) {
        if(target / (tickIntervals[i - 1][0].duration * tickIntervals[i - 1][1]) < tickIntervals[i][0].duration * tickIntervals[i][1] / target)
            ret = tickIntervals[i - 1];
        else
            ret = tickIntervals[i];
    } else {
        ret = [$time.millisecond, linearTickStep(a, b, n)];
    }
    return [ret[0], a < b ? ret[1] : -ret[1]];
}

interface CHMapper<T, U> {
    normalize(x: T): U;
    denormalize(x: U): T;
}

export interface CHScale<T> extends CHMapper<T, number> {
    scale(min: T, max: T): void;
    showGrid(): boolean;
    ticks(this: CHScale<T>, n: number): T[];
    tickFormat: (this: CHScale<T>, x: T) => string;
    tipFormat: (this: CHScale<T>, x: T) => string;
}

export class CHLinearScale implements CHScale<number> {
    private _a!: number;
    private _b!: number;
    private _d!: number;

    private _digits: number;

    constructor(a: number = 0, b: number = 1) {
        this.scale(a, b);

        this._digits = 0;
    }

    scale(a: number, b: number) {
        if(a === b)
            b = a + 1;
        this._a = a;
        this._b = b;
        this._d = b - a;
    }

    normalize(x: number) {
        return (x - this._a) / this._d;
    }

    denormalize(x: number) {
        return this._a + this._d * x;
    }

    showGrid() {
        return true;
    }

    ticks(n: number) {
        const step = linearTickStep(this._a, this._b, n, true);
        const abs = Math.abs(step);
        this._digits = abs > 0 ? -Math.floor(Math.log(abs) * Math.LOG10E) : 0;
        return linearRange(Math.ceil(this._a / step) * step, Math.floor(this._b / step) * step + step / 2, step);
    }

    tickFormat(n: number) {
        return n.toFixed(this._digits > 0 ? this._digits : 0);
    }

    tipFormat(n: number) {
        return n.toFixed(this._digits > -1 ? this._digits + 1 : 0);
    }

    //sets the domain to nice values!!!
    //   scale.nice = function(count) {
    //     const d = domain(),
    //         i = d.length - 1,
    //         n = count == null ? 10 : count,
    //         start = d[0],
    //         stop = d[i],
    //         step = tickStep(start, stop, n);

    //     if (step) {
    //       step = tickStep(Math.floor(start / step) * step, Math.ceil(stop / step) * step, n);
    //       d[0] = Math.floor(start / step) * step;
    //       d[i] = Math.ceil(stop / step) * step;
    //       domain(d);
    //     }

    //     return scale;
    //   };
}

export class CHNameScale implements CHScale<number> {
    private _names: string[];

    constructor(names: string[]) {
        this._names = names;
    }

    scale(_a: number, _y: number) {

    }

    normalize(x: number) {
        return (x + 0.5) / this._names.length;
    }

    denormalize(x: number) {
        return Math.round(x * this._names.length - 0.5);
    }

    showGrid() {
        return false;
    }

    ticks(_n: number) {
        return this._names.map((_, i) => i);
    }

    tickFormat(n: number) {
        return this._names[Math.floor(n)];
    }

    tipFormat(n: number) {
        return this._names[Math.floor(n)];
    }
    //sets the domain to nice values!!!
    //   scale.nice = function(count) {
    //     const d = domain(),
    //         i = d.length - 1,
    //         n = count == null ? 10 : count,
    //         start = d[0],
    //         stop = d[i],
    //         step = tickStep(start, stop, n);

    //     if (step) {
    //       step = tickStep(Math.floor(start / step) * step, Math.ceil(stop / step) * step, n);
    //       d[0] = Math.floor(start / step) * step;
    //       d[i] = Math.ceil(stop / step) * step;
    //       domain(d);
    //     }

    //     return scale;
    //   };
}

export class CHTimeScale implements CHScale<number> {
    private _a!: number;
    private _b!: number;
    private _dn!: number;

    tickFormat!: (x: number) => string;
    tip_format: string;

    constructor(a: number = 0, b: number = 1) {
        this.scale(a, b);
        this.tip_format = 'EEE d MMM';
    }

    scale(a: number, b: number) {
        this._a = a;
        this._b = b;
        this._dn = b - a;

        this.tickFormat = t => {
            const y = $time(t);
            return y.format(y.milliseconds ? '.sss' : y.seconds ? ':ss' : y.minutes ? 'HH:mm' : y.hours ? 'HH:mm' : (y.day_of_month > 1) ?
                'EEE d' : y.month ? 'MMMM yyyy' : 'yyyy');
        }
    }

    tipFormat(x: number) {
        return $time(x).format(this.tip_format);
    }

    normalize(x: number) {
        return (x - this._a) / this._dn;
    }

    denormalize(x: number) {
        return this._a + this._dn * x;
    }

    showGrid() {
        return true;
    }

    ticks(n: number) {
        const t = timeTickInterval(this._a, this._b, n);
        return $time.range($time(this._a), $time(this._b), t[0], t[1]).map(t => t.n);
    }
}

export class CHColorMapper implements CHMapper<number, UIColor> {
    private _a: UIColor;
    private _b: UIColor;

    constructor(a: UIColor, b?: UIColor) {
        if(!b) {
            a = $color.HSL(a, true);
            b = $color.clone(a);
            a[2] = Math.max(0, a[2] - 0.1);
            b[2] = Math.min(1, b[2] + 0.2);
        }

        this._a = a;
        this._b = b;
    }

    normalize(x: number) {
        return $color.mix(this._a, this._b, x);
    }

    denormalize(_x: UIColor) {
        return 0;
    }
}
